<div class="feature-flag-detail">
  <div class="loading-indicator" *ngIf="loading"><span class="spinner"></span> Loading feature flag...</div>

  <div class="error-message" *ngIf="errorMessage">
    <p>{{ errorMessage }}</p>
    <button id="e2e-retry-load" mat-button color="primary" (click)="loadFeatureFlag()">Retry</button>
  </div>

  <div class="flag-detail-content" *ngIf="flag && !loading">
    <!-- Header -->
    <div class="flag-header">
      <div class="flag-title">
        <h2>{{ flag.name }}</h2>
        <span class="flag-id" (click)="copyToClipboard(flag.id)">{{ flag.id }}</span>
        <span class="status-badge" [ngClass]="flag.status">
          {{ flag.status }}
        </span>
      </div>
      <div class="flag-actions">
        <button
          id="e2e-edit-flag"
          mat-flat-button
          color="primary"
          (click)="onEditFlag()"
          *ngIf="flag.status === 'active'"
        >
          Edit Flag
        </button>
        <button
          id="e2e-archive-flag"
          mat-flat-button
          color="primary"
          (click)="onArchiveFlag()"
          *ngIf="flag.status === 'active'"
        >
          Archive Flag
        </button>
      </div>
    </div>

    <!-- Details -->
    <div class="flag-details">
      <div class="detail-card">
        <h3>General Information</h3>
        <div class="detail-grid">
          <div class="detail-item">
            <label>Description</label>
            <span>{{ flag.description }}</span>
          </div>
          <div class="detail-item">
            <span
              >Created By {{ flag.createdBy }} on <span>{{ formatDate(flag.createdAt) }}</span></span
            >
          </div>
          <div class="detail-item">
            <span
              >Updated By {{ flag.updatedBy }} <span>{{ formatDate(flag.updatedAt) }}</span></span
            >
          </div>
        </div>
      </div>

      <div class="detail-card rule-card">
        <h3>
          Rule Configuration
          <button mat-button color="primary" (click)="toggleRuleExpansion()">
            {{ expandedRule ? 'Collapse' : 'Expand' }}
          </button>
        </h3>

        <div class="rule-type-badge">
          {{ flag.rule.type }}
        </div>

        <!-- Simple rule visualization -->
        <div class="rule-visualization" *ngIf="!expandedRule">
          <ng-container [ngSwitch]="flag.rule.type">
            <div *ngSwitchCase="'boolean'" class="boolean-rule">
              <span>{{ flag.rule.value ? 'Always ENABLED' : 'Always DISABLED' }}</span>
            </div>
            <div *ngSwitchCase="'email'" class="email-rule">
              <span>Enabled for {{ flag.rule.values.length }} email(s)</span>
              <ul *ngIf="flag.rule.values.length <= 3">
                <li *ngFor="let email of flag.rule.values">{{ email }}</li>
              </ul>
              <p *ngIf="flag.rule.values.length > 3">
                {{ flag.rule.values.slice(0, 3).join(', ') }} and {{ flag.rule.values.length - 3 }} more
              </p>
            </div>
            <div *ngSwitchCase="'userGroup'" class="user-group-rule">
              <span>Enabled for {{ flag.rule.values.length }} user group(s)</span>
              <ul *ngIf="flag.rule.values.length <= 5">
                <li *ngFor="let group of flag.rule.values">{{ group }}</li>
              </ul>
              <p *ngIf="flag.rule.values.length > 5">
                {{ flag.rule.values.slice(0, 5).join(', ') }} and {{ flag.rule.values.length - 5 }} more
              </p>
            </div>

            <div *ngSwitchCase="'userRole'" class="user-group-rule">
              <span>Enabled for {{ flag.rule.values.length }} user role(s)</span>
              <ul *ngIf="flag.rule.values.length <= 5">
                <li *ngFor="let item of flag.rule.values">{{ item }}</li>
              </ul>
              <p *ngIf="flag.rule.values.length > 5">
                {{ flag.rule.values.slice(0, 5).join(', ') }} and {{ flag.rule.values.length - 5 }} more
              </p>
            </div>

            <div *ngSwitchCase="'region'" class="region-rule">
              <span>Enabled for {{ flag.rule.values.length }} region(s)</span>
              <ul *ngIf="flag.rule.values.length <= 5">
                <li *ngFor="let region of flag.rule.values">{{ region }}</li>
              </ul>
              <p *ngIf="flag.rule.values.length > 5">
                {{ flag.rule.values.slice(0, 5).join(', ') }} and {{ flag.rule.values.length - 5 }} more
              </p>
            </div>
            <div *ngSwitchCase="'lineOfBusiness'" class="lob-rule">
              <span>Enabled for {{ flag.rule.values.length }} business line(s)</span>
              <ul *ngIf="flag.rule.values.length <= 5">
                <li *ngFor="let lob of flag.rule.values">{{ lob }}</li>
              </ul>
              <p *ngIf="flag.rule.values.length > 5">
                {{ flag.rule.values.slice(0, 5).join(', ') }} and {{ flag.rule.values.length - 5 }} more
              </p>
            </div>
            <div *ngSwitchCase="'group'" class="group-rule">
              <span
                >Complex rule using {{ flag.rule.operator }} operator with
                {{ flag.rule.childRules.length }} conditions</span
              >
            </div>
            <div *ngSwitchDefault>
              <span>Unknown rule type</span>
            </div>
          </ng-container>
        </div>

        <!-- Expanded JSON rule -->
        <ng-container *ngIf="expandedRule">
          <button
            id="e2e-copy-json"
            mat-button
            color="primary"
            class="fx--ml-sm"
            (click)="copyToClipboard(getPrettyRule())"
          >
            Copy JSON
          </button>
          <div class="rule-json">
            <pre>{{ getPrettyRule() }}</pre>
          </div>
        </ng-container>
      </div>

      <!-- Evaluation Testing Panel -->
      <div class="detail-card evaluation-card" *ngIf="showEvaluation">
        <h3>Evaluate Flag</h3>
        <p class="evaluation-intro">
          Test this feature flag against a user context to see whether it would be enabled.
        </p>

        <div class="evaluation-editor">
          <label for="context-input">User Context (JSON):</label>
          <textarea id="context-input" [formControl]="evaluationContext" rows="10" class="context-input"></textarea>
        </div>

        <div class="evaluation-actions">
          <button id="e2e-evuate-flag" mat-flat-button color="primary" (click)="evaluateFlag()" [disabled]="evaluating">
            <span class="spinner" *ngIf="evaluating"></span>
            {{ evaluating ? 'Evaluating...' : 'Evaluate' }}
          </button>
        </div>

        <div class="evaluation-error" *ngIf="evaluationError">
          <p>{{ evaluationError }}</p>
        </div>

        <div class="evaluation-result" *ngIf="evaluationResult !== null && !evaluationError">
          <h4>Evaluation Result:</h4>
          <div class="result-badge" [ngClass]="evaluationResult ? 'enabled' : 'disabled'">
            {{ evaluationResult ? 'ENABLED' : 'DISABLED' }}
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
